import {
  addHours,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns'
import { useEffect } from 'react'
import { useTheme } from 'styled-components'

import { RootState, useStore } from '../../../application/store/store'
import { DashboardPeriod } from '../../../domain/dashboard.types'
import {
  DASHBOARD_DATA_TYPE_DISPLAYED,
  DASHBOARD_PERIOD,
} from '../../../domain/enums/dashboard.enum'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import { useAnalytics } from './useAnalytics'
import { useGraphData } from './useGraphData'
import { useTimeData } from './useTimeData'

export const useDashBoardHook = () => {
  const { isMobile } = useTheme()
  const { t } = useCustomTranslation('common')

  const userAccessState = useStore((state: RootState) => state.user.access)
  const salesRevenue = useStore((state: RootState) => state.salesRevenue)
  const selectedPlant = useStore((state: RootState) => state.selectedPlant)
  const documents = useStore((state: RootState) => state.documents)

  const {
    interval,
    optionsDatePicker,
    periodSelected,
    selectedDate,
    setOptionsDatePicker,
    setSelectedDate,
    setPeriodSelected,
    datePickerParamsBuilder,
    intervalSetter,
  } = useTimeData()
  const {
    expectedProduction,
    powerGraphPoints,
    energyGraphPoints,
    graphDataLoader,
    displayPowerOrEnergy,
    graphDataByDay,
    graphDataByMonth,
    graphDataByWeek,
    graphDataByYear,
    graphDataBuilder,
    setDisplayPowerOrEnergy,
  } = useGraphData({
    selectedPlant,
    periodSelected,
  })

  const { productionData, analyticsDataLoader, fetchDocuments, fetchSalesRevenue } = useAnalytics({
    energyGraphPoints,
    selectedPlant,
  })

  // Compute global loading state from individual loading states
  const loaders = {
    ...graphDataLoader,
    ...analyticsDataLoader,
  }
  const isGlobalLoading = Object.keys(loaders).every((key) => !loaders[key as keyof typeof loaders])

  useEffect(() => {
    if (userAccessState?.dashboardCaStat) {
      fetchDocuments()
    }
  }, [userAccessState?.dashboardCaStat, selectedPlant])

  useEffect(() => {
    // Fetch initial data for the selected period
    if (periodSelected !== DASHBOARD_PERIOD.DAY) return
    setOptionsDatePicker(datePickerParamsBuilder(periodSelected))
    const dateStart = startOfDay(selectedDate).valueOf()
    const dateEnd = endOfDay(selectedDate).valueOf()
    graphDataByDay(dateStart, dateEnd)
    fetchSalesRevenue(dateStart, dateEnd)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlant])

  // Update graph data based on selected period and date
  const updateDataForPeriod = (period: DashboardPeriod, date: number) => {
    if (!userAccessState?.dashboardProductionStat) {
      return
    }
    intervalSetter(period)
    setOptionsDatePicker(datePickerParamsBuilder(period))
    const dateStart = startOfDay(date).valueOf()
    const dateEnd = endOfDay(date).valueOf()

    switch (period) {
      case DASHBOARD_PERIOD.DAY: {
        graphDataByDay(dateStart, dateEnd)
        fetchSalesRevenue(dateStart, dateEnd)
        break
      }
      case DASHBOARD_PERIOD.WEEK: {
        const startWeek = addHours(startOfWeek(dateStart, { weekStartsOn: 1 }), 2).valueOf()
        const endWeek = addHours(endOfWeek(dateStart, { weekStartsOn: 1 }), 1).valueOf()
        graphDataByWeek(startWeek, endWeek)
        fetchSalesRevenue(startWeek, endWeek)
        break
      }
      case DASHBOARD_PERIOD.MONTH: {
        // Add two hours to the dateStart to be sure to get the first day of the month due to the difference between GMT and local time
        const startMonth = addHours(startOfMonth(dateStart), 2).valueOf()
        const endMonth = endOfMonth(dateStart).valueOf()
        graphDataByMonth(startMonth, endMonth)
        fetchSalesRevenue(startMonth, endMonth)
        break
      }
      case DASHBOARD_PERIOD.YEAR: {
        const startYear = addHours(startOfYear(dateStart), 2).valueOf()
        const endYear = endOfYear(dateStart).valueOf()
        graphDataByYear(startYear, endOfMonth(subMonths(dateEnd, 1)).valueOf(), endYear)
        fetchSalesRevenue(startYear, endYear)
        break
      }
    }
  }

  // Function to handle period change (1J 7J 1M 1A)
  const handleChangePeriod = async (period: DashboardPeriod) => {
    setDisplayPowerOrEnergy(DASHBOARD_DATA_TYPE_DISPLAYED.ENERGY)
    setPeriodSelected(period)
    const currentDate = Date.now()
    let formattedDate: number
    switch (period) {
      case DASHBOARD_PERIOD.DAY: {
        setDisplayPowerOrEnergy(DASHBOARD_DATA_TYPE_DISPLAYED.POWER)
        formattedDate = subDays(currentDate, 1).valueOf()
        break
      }
      case DASHBOARD_PERIOD.WEEK: {
        formattedDate = addHours(startOfWeek(currentDate, { weekStartsOn: 1 }), 2).valueOf()
        break
      }
      case DASHBOARD_PERIOD.MONTH: {
        formattedDate = addHours(startOfMonth(currentDate), 2).valueOf()
        break
      }
      case DASHBOARD_PERIOD.YEAR: {
        formattedDate = currentDate
        break
      }
      default:
        formattedDate = currentDate
        break
    }
    setSelectedDate(formattedDate)
    updateDataForPeriod(period, formattedDate)
  }

  // Function to update data when date is selected from the date picker
  const selectDateFromDatePicker = (date: number) => {
    setDisplayPowerOrEnergy(DASHBOARD_DATA_TYPE_DISPLAYED.POWER) // Reset to power when date is selected
    setSelectedDate(date)
    updateDataForPeriod(periodSelected, date)
  }

  const handleDisplayEnergyOrPower = (dataType: DASHBOARD_DATA_TYPE_DISPLAYED) => {
    setDisplayPowerOrEnergy(dataType)
  }

  return {
    isMobile,
    selectedPlant,
    selectedDate,
    optionsDatePicker,
    userAccessState,
    salesRevenue,
    energyGraphPoints,
    expectedProduction,
    powerGraphPoints,
    displayPowerOrEnergy,
    documents,
    periodSelected,
    interval,
    isGlobalLoading,
    analyticsDataLoader,
    graphDataLoader,
    formatedData: productionData,
    graphDataBuilder,
    handleChangePeriod,
    selectDateFromDatePicker,
    handleDisplayEnergyOrPower,
    t,
  }
}
